import AdminSidebar from "../../components/admin/AdminSidebar"
import AdminHeader from "../../components/admin/AdminHeader"
import { useNavigate } from "react-router-dom"
import { useGet } from "../../utils/apiComponent/useApi"
import { endpoints } from "../../utils/endpoints"
import { Button } from "../../components/ui/button"
import { Plus, Edit, Trash2, Eye } from "lucide-react"
import { api } from "../../utils/apiComponent/apiClient"
import { toastUtils } from "../../utils/toast"

export default function CourseManagementPage() {
  const navigate = useNavigate()

  const {
    data: coursesData,
    loading,
    error,
    execute: refetchCourses
  } = useGet(endpoints.courses)

  const handleCreateCourse = () => {
    navigate("/admin/courses/new/editor")
  }

  const handleEditCourse = (courseId) => {
    navigate(`/admin/courses/${courseId}/edit`)
  }

  const handleViewCourse = (courseId) => {
    navigate(`/courses/${courseId}`)
  }

  const handleDeleteCourse = async (courseId, courseTitle) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le cours "${courseTitle}" ? Cette action est irréversible et supprimera également tous les modules et blocs associés.`)) {
      return
    }
    
    try {
      console.log('[CourseManagementPage] Suppression du cours:', courseId)
      const response = await api.delete(endpoints.deleteCourse(courseId))
      console.log('[CourseManagementPage] Réponse de suppression:', response)
      console.log('[CourseManagementPage] Cours supprimé:', courseTitle)
      
      toastUtils.success("Cours supprimé avec succès !")
      
      // Attendre un peu avant de rafraîchir pour que le backend enregistre la suppression
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Rafraîchir la liste des cours
      await refetchCourses()
      console.log('[CourseManagementPage] Liste rafraîchie après suppression')
    } catch (err) {
      console.error('[CourseManagementPage] Erreur suppression:', err)
      console.error('[CourseManagementPage] Détails erreur:', {
        message: err.message,
        status: err.status,
        details: err.details
      })
      toastUtils.error(err?.message || "Erreur lors de la suppression du cours")
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen">
        <AdminSidebar />
        <div className="flex-1 ml-64 p-8">
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#141835] mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des cours...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-screen">
        <AdminSidebar />
        <div className="flex-1 ml-64 p-8">
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
            <p className="text-gray-800">Erreur lors du chargement des cours</p>
          </div>
        </div>
      </div>
    )
  }

  // Extract courses from response - handle both array and object responses
  const coursesList = Array.isArray(coursesData) 
    ? coursesData 
    : (coursesData?.courses || coursesData?.data?.courses || [])

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <AdminHeader 
          title="Gestion des cours" 
          subtitle="Créer, modifier et gérer les cours"
          showCreateButton={true}
          createButtonText="Créer un cours"
          onCreateClick={handleCreateCourse}
        />

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {coursesList.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">Aucun cours disponible</p>
              <Button onClick={handleCreateCourse} className="bg-[#203a8a] hover:bg-[#1a1f4a]">
                <Plus className="w-4 h-4 mr-2" />
                Créer votre premier cours
              </Button>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Titre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Catégorie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prix
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {coursesList.map((course) => (
                  <tr key={course.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{course.title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{course.category?.name || "N/A"}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {course.price ? `${course.price} ${course.currency || "XOF"}` : "Gratuit"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        course.isPublished ? "bg-blue-50 text-[#203a8a]" : "bg-gray-100 text-gray-800"
                      }`}>
                        {course.isPublished ? "Publié" : "Brouillon"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewCourse(course.id)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditCourse(course.id)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCourse(course.id, course.title)}
                          className="text-gray-700 hover:text-gray-900 hover:text-red-600"
                          title="Supprimer le cours"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

